import { TemplateRef, Type } from '@angular/core';
import { hasSectionValidatorFn } from './section-composition-fns';
import { HasValidatorFn } from './has-validator-fn';
import { SmallSection } from '../services';
import {
  ControlListenerPlugin,
  ControlValidatorPlugin,
  FormControlMaker,
  FormControlOptions,
  FormRegisterableComponent,
  FormRegisterableComponentPlugin,
} from './control-types';
import { Data } from './data';
import { Section } from './section';
import { BusinessValidationRules } from './validation-rules';
import { ValidatorFn } from '@angular/forms';

export interface GroupedSection<T = Section> {
  /**
   * Resolved from parent section id that was identified as parent section
   * (parentSectionId <= section.sectionId === section.parentSectionId ? section.sectionId)
   */
  parentSectionId: number;
  /**
   * Section set identified as part of this group (all share same parentSectionId)
   */
  sections: T[];
  /**
   * @deprecated in favor of `Component`
   * This is acquired if parent section has `groupTemplateRefName`
   */
  templateRefName?: string;
  /**
   * @deprecated in favor of `Component`
   * Resolved templateRef from templateRefName
   */
  templateRef?: TemplateRef<any>;
  /**
   * acquired from parent section.title
   */
  title?: string;
  /**
   * Section position, resolved from index in the section list
   */
  position?: number;
  /**
   * whether if main section is required
   */
  required?: boolean;
  /**
   * @deprecated in favor of `FormControlOptions.validatorFn`
   * Business rules or expressions returning error messages at group level
   */
  validityRules?: BusinessValidationRules;
  /**
   * FormControlOptions
   */
  formControlOptions?: FormControlOptions<T>;
  /**
   * FormControlOptions by name
   */
  targetedControlOptions?: { [key: string]: FormControlOptions<T> };
  /**
   * FormRegisterableComponent<Section>
   */
  Component?: Type<FormRegisterableComponent<T>>;
  /**
   * Used to store properties that are required by another component
   */
  componentConfig?: Data;
  /**
   * Original section
   */
  originalSection?: SmallSection;
  /**
   * Usage Target is an array of valid usages used to show/hide the grouped Section.
   */
  usageTarget?: string[];
  /**
   * This listener is used to set custom Listener
   */
  controlListener?: Type<ControlListenerPlugin<any>>;
  /**
   * This service will replace the configured in the component to build/find his
   * targeted control in the main form
   */
  controlMaker?: Type<FormControlMaker<any>>;
  /**
   * This service is used to create and do control.setValidator to given tuples of control/config
   */
  validatorPlugin?: Type<ControlValidatorPlugin<any>>;
  /**
   * validatorPlugin configs
   */
  validatorPluginConfig?: any;
  /**
   * Placeholder for hasValidatorFn composition
   */
  validatorFns?: () => ValidatorFn[];
  /**
   * Link Generator
   */
  linkGenerator?: (data: Data) => string[];
  /**
   * Service to add behavior to given component
   */
  ComponentPlugin?: {
    Type: Type<FormRegisterableComponentPlugin<GroupedSection>>;
    options?: Data;
  };
}

/**
 * Transforms RawColumn to Column
 * @param column - the db or raw column
 */
export const GroupSectionFactory = <T extends GroupedSection>(groupedSection: T): T & HasValidatorFn => {
  return Object.assign(groupedSection, hasSectionValidatorFn());
};
